/**
 * EquipmentStatusTransitionModal — a `equipment/01-unit-editor` státusz-átmenet domain-action modal.
 *
 * A proto (`01-unit-editor.html`) státusz-modalja: **target-status grid** (a `canTransition`/
 * `EQUIPMENT_UNIT_TRANSITIONS` állapotgéppel gate-elve a jelenlegi státuszból) + conditional indoklás
 * (≥20 char a `UNIT_STATUS_REASON_REQUIRED`-nál) + conditional fotó (`UNIT_STATUS_PHOTO_REQUIRED` =
 * DAMAGED) + audit-note + confirm-disabled-until-valid. → `POST /equipment-units/:id/status`.
 *
 * NB: a `@kgc/ui` StatusTransitionModal single-`to` + 4-state `StatusBadge` → a proto 8-state
 * target-grid-jéhez custom FE-modal (measured-recon), a `@kgc/ui` Dialog/Textarea/PhotoUpload reuse-szal.
 */
import { transitionEquipmentUnitStatus } from '@/api/equipment-unit';
import {
  EQUIPMENT_UNIT_TRANSITIONS,
  UNIT_STATUS_PHOTO_REQUIRED,
  UNIT_STATUS_REASON_REQUIRED,
  type EquipmentUnitStatus,
} from '@kgc/equipment-type-domain';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PhotoUpload,
  Textarea,
  toast,
  type UploadedPhoto,
} from '@kgc/ui';
import { useState } from 'react';

import { mockEquipmentPhotoAdapter } from './equipment-photo-adapter';
import { UnitStatusBadge } from './unit-status-badge';

const REASON_MIN = 20;

export function EquipmentStatusTransitionModal({
  open,
  onOpenChange,
  unitId,
  currentStatus,
  onTransitioned,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId: string;
  currentStatus: EquipmentUnitStatus;
  onTransitioned: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<EquipmentUnitStatus | null>(null);
  const [reason, setReason] = useState('');
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const allowedTargets = EQUIPMENT_UNIT_TRANSITIONS[currentStatus];
  const reasonRequired =
    target !== null && (UNIT_STATUS_REASON_REQUIRED as readonly string[]).includes(target);
  const photoRequired =
    target !== null && (UNIT_STATUS_PHOTO_REQUIRED as readonly string[]).includes(target);
  const reasonValid = !reasonRequired || reason.trim().length >= REASON_MIN;
  const photoValid = !photoRequired || photos.length >= 1;
  const canConfirm = target !== null && reasonValid && photoValid && !submitting;

  function reset() {
    setTarget(null);
    setReason('');
    setPhotos([]);
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleConfirm() {
    if (!target) return;
    setSubmitting(true);
    try {
      await transitionEquipmentUnitStatus(unitId, {
        targetStatus: target,
        ...(reasonRequired ? { reason: reason.trim() } : {}),
        ...(photoRequired ? { photoIds: photos.map(p => p.id) } : {}),
      });
      toast.success(t('equipmentUnit.transition.success'));
      reset();
      onOpenChange(false);
      await onTransitioned();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('equipmentUnit.transition.error'));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="eu-transition-modal">
        <DialogHeader>
          <DialogTitle>{t('equipmentUnit.transition.title')}</DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <UnitStatusBadge status={currentStatus} size="sm" />
            <span aria-hidden>→</span>
            {target ? (
              <UnitStatusBadge status={target} size="sm" />
            ) : (
              <span className="text-sm text-muted-foreground">
                {t('equipmentUnit.transition.targetLabel')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Target-status grid (canTransition-gate) */}
          {allowedTargets.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('equipmentUnit.transition.noTargets')}
            </p>
          ) : (
            <div
              className="grid grid-cols-2 gap-2 sm:grid-cols-3"
              data-testid="eu-transition-targets"
            >
              {allowedTargets.map(ts => (
                <button
                  key={ts}
                  type="button"
                  data-testid={`eu-transition-target-${ts}`}
                  aria-pressed={target === ts}
                  onClick={() => setTarget(ts)}
                  className={`rounded-md border p-2 text-left transition ${
                    target === ts
                      ? 'border-primary ring-2 ring-primary/40'
                      : 'border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800'
                  }`}
                >
                  <UnitStatusBadge status={ts} size="sm" />
                </button>
              ))}
            </div>
          )}

          {/* Conditional reason */}
          {reasonRequired && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('equipmentUnit.transition.reasonLabel')}{' '}
                <span className="text-destructive">{t('equipmentUnit.transition.required')}</span>
              </label>
              <Textarea
                data-testid="eu-transition-reason"
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
              />
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {t('equipmentUnit.transition.reasonHint', { count: reason.trim().length })}
              </p>
            </div>
          )}

          {/* Conditional photo (DAMAGED) */}
          {photoRequired && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('equipmentUnit.transition.photoLabel')}{' '}
                <span className="text-destructive">{t('equipmentUnit.transition.required')}</span>
              </label>
              <PhotoUpload
                value={photos}
                onChange={setPhotos}
                adapter={mockEquipmentPhotoAdapter}
              />
            </div>
          )}

          <p className="rounded-md bg-gray-50 p-2 text-[11px] text-muted-foreground dark:bg-gray-800/60">
            {t('equipmentUnit.transition.auditNote')}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="eu-transition-confirm"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {t('equipmentUnit.transition.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
